import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  dateToISO,
  isoToDate,
  formatDMY,
  parseDMY,
} from "@/lib/dateUtils";

/**
 * Auto-format numeric input as the user types:
 *   "0"     -> "0"
 *   "01"    -> "01"
 *   "012"   -> "01.2"
 *   "0102"  -> "01.02"
 *   "01023" -> "01.02.3"
 *   "01022025" -> "01.02.2025"
 * If the user pastes/types letters or dots, they are stripped and re-inserted.
 */
const autoFormat = (raw) => {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
};

/**
 * DateField with auto-dot insertion and optional default year auto-append.
 * value: 'YYYY-MM-DD' | null
 * onChange: (iso | null) => void
 * defaultYear: number - if user types only DD.MM, this year is appended on commit
 */
export default function DateField({
  value,
  onChange,
  placeholder = "DD.MM.",
  testId,
  minDate,
  maxDate,
  defaultYear,
  onEnter,
  inputRef,
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value ? formatDMY(value) : "");

  // Sync from external value changes (e.g. calendar pick, sample load, reset)
  useEffect(() => {
    setText(value ? formatDMY(value) : "");
  }, [value]);

  const commitText = (raw) => {
    let val = (raw || "").trim();
    // If user typed only DD.MM (5 chars incl. dot) — append default year
    if (/^\d{2}\.\d{2}$/.test(val) && defaultYear) {
      val = `${val}.${defaultYear}`;
    }
    // If user typed DD.MM.YY (2-digit year) — expand to 4-digit using century of defaultYear
    const shortY = val.match(/^(\d{2})\.(\d{2})\.(\d{2})$/);
    if (shortY && defaultYear) {
      const century = Math.floor(defaultYear / 100) * 100;
      val = `${shortY[1]}.${shortY[2]}.${century + parseInt(shortY[3], 10)}`;
    }

    const iso = parseDMY(val);
    if (iso) {
      onChange(iso);
      setText(formatDMY(iso));
    } else if (val === "") {
      onChange(null);
      setText("");
    } else {
      // Invalid: snap back to last valid value
      setText(value ? formatDMY(value) : "");
    }
  };

  return (
    <div className="flex items-stretch gap-0">
      <Input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(autoFormat(e.target.value))}
        onBlur={(e) => commitText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            // Compute the ISO synchronously so we can hand it to onEnter,
            // even if parent state has not yet re-rendered.
            let raw = (e.currentTarget.value || "").trim();
            if (/^\d{2}\.\d{2}$/.test(raw) && defaultYear) {
              raw = `${raw}.${defaultYear}`;
            }
            const shortY = raw.match(/^(\d{2})\.(\d{2})\.(\d{2})$/);
            if (shortY && defaultYear) {
              const century = Math.floor(defaultYear / 100) * 100;
              raw = `${shortY[1]}.${shortY[2]}.${century + parseInt(shortY[3], 10)}`;
            }
            const iso = parseDMY(raw);
            if (iso) {
              onChange(iso);
              setText(formatDMY(iso));
              if (onEnter) onEnter(iso);
            } else {
              commitText(e.currentTarget.value);
            }
          }
        }}
        className={cn(
          "font-mono-terminal bg-[#0C0E14] border-[#272A35] text-slate-100",
          "rounded-r-none focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]",
        )}
        maxLength={10}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="rounded-l-none border-l-0 border-[#272A35] bg-[#1A1E2C] hover:bg-[#272A35] px-3"
            data-testid={testId ? `${testId}-picker` : undefined}
            aria-label="Otvori kalendar"
          >
            <CalendarIcon className="h-4 w-4 text-[#D4AF37]" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 bg-[#131620] border-[#272A35]"
          align="end"
        >
          <Calendar
            mode="single"
            selected={isoToDate(value)}
            defaultMonth={
              isoToDate(value) ||
              (defaultYear ? new Date(defaultYear, new Date().getMonth(), 1) : undefined)
            }
            onSelect={(d) => {
              if (d) {
                onChange(dateToISO(d));
                setOpen(false);
              }
            }}
            disabled={(d) => {
              if (minDate && dateToISO(d) < minDate) return true;
              if (maxDate && dateToISO(d) > maxDate) return true;
              return false;
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
